import React from 'react';
import SoundWave from './SoundWave';

const RetroCassette = ({ 
  title = "FAMILY BIBLE", 
  isRecording = false, 
  isPaused = false,
  onRecordClick, 
  onStopClick,
  showControls = true,
  // New props for verse selection
  selectedBook,
  selectedChapter,
  startVerse,
  endVerse,
  availableBooks,
  availableChapters,
  availableVerses,
  availableEndVerses,
  onBookChange,
  onChapterChange,
  onStartVerseChange,
  onEndVerseChange,
  // Timer prop
  recordingTime = 0,
  // Plan name prop
  planName = null,
  // Verses recorded / total for the current scope (chapter or whole plan)
  recordedCount = null,
  totalVerses = null,
  // Active Bible translation (microtext under the reference line)
  translationName = '',
  // Compact/slim mode — drops the decorative title strip, reels, and color
  // bars down to a single selector row + status row, so more of the screen
  // stays free for surrounding content (e.g. a Passages Plan list) on
  // mobile. Mirrors the mobile app's cassetteCompact preference — same
  // Firestore field, ported to a web-appropriate layout rather than a
  // literal 1:1 visual port.
  compact = false
}) => {
  const selectorRow = (
    <div className="flex items-center space-x-1 sm:space-x-2 sm:space-x-3 text-xs sm:text-sm">
      <select
        value={selectedBook}
        onChange={onBookChange}
        className="w-32 sm:w-40 lg:w-44 bg-white border border-gray-300 rounded px-1 sm:px-2 py-0.5 sm:py-1 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left text-xs sm:text-sm"
      >
        {availableBooks?.map(book => (
          <option key={book.id} value={book.id}>{book.name}</option>
        ))}
      </select>
      <select
        value={selectedChapter}
        onChange={onChapterChange}
        className="w-12 sm:w-16 bg-white border border-gray-300 rounded pl-1 pr-4 sm:pr-6 py-0.5 sm:py-1 text-gray-700 text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm"
      >
        {availableChapters?.map(ch => (
          <option key={ch} value={ch}>{ch}</option>
        ))}
      </select>
      <span className="text-gray-400 px-0.5 sm:px-1">:</span>
      <select
        value={startVerse}
        onChange={onStartVerseChange}
        className="w-12 sm:w-16 bg-white border border-gray-300 rounded pl-1 pr-4 sm:pr-6 py-0.5 sm:py-1 text-gray-700 text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm"
      >
        {availableVerses?.map(v => (
          <option key={v} value={v}>{v}</option>
        ))}
      </select>
      <span className="text-gray-400 px-0.5 sm:px-1">-</span>
      <select
        value={endVerse}
        onChange={onEndVerseChange}
        className="w-12 sm:w-16 bg-white border border-gray-300 rounded pl-1 pr-4 sm:pr-6 py-0.5 sm:py-1 text-gray-700 text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm"
      >
        {availableEndVerses?.map(v => (
          <option key={v} value={v}>{v}</option>
        ))}
      </select>
    </div>
  );

  if (compact) {
    return (
      <div className="flex justify-center mb-2 sm:pt-2">
        <div className="w-full max-w-[540px] border-2 border-[#4A9BC7] rounded-xl bg-[#063354] p-2.5 flex flex-col gap-2">
          <div className="w-full bg-white rounded-lg flex items-center justify-center py-2">
            {selectorRow}
          </div>
          <div className="w-full flex items-center justify-between px-1">
            {(recordedCount !== null && totalVerses !== null) ? (
              <div className="text-xs text-white/80 font-medium">
                <span className="text-cerulean-lighter"><strong>{recordedCount}</strong> of <strong>{totalVerses}</strong> verses recorded</span>
              </div>
            ) : <span />}
            <div className="flex items-center gap-2">
              <SoundWave
                isActive={isRecording && !isPaused}
                barCount={4}
                height="14px"
                color="#4A90E2"
              />
              {(isRecording || isPaused) && (
                <span className="text-lg font-mono font-bold text-white">
                  {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center mb-2 lg:mb-6 sm:pt-6">
      <div className="relative w-full max-w-[540px]">
        {/* Main Cassette Container */}
        <div className="w-full h-[320px] sm:h-[380px] bg-[#063354] border-[16px] sm:border-[20px] border-[#063354] rounded-md relative overflow-hidden shadow-lg">
          {/* Verse Selection - Above the title */}
          <div className="w-full h-12 sm:h-16 bg-white relative flex items-center justify-center" style={{ borderRadius: '9px 9px 0 0' }}>
            {selectorRow}
          </div>

          {/* Cassette Label - Top portion with title */}
          <div className="w-full h-12 sm:h-16 bg-[#ebebeb] relative">
            {/* Title + translation underneath */}
            <div className="w-full h-full flex flex-col items-center justify-center leading-none pt-1 sm:pt-1.5">
              <div className="text-center text-2xl sm:text-4xl text-space-cadet uppercase tracking-wider" style={{ fontFamily: 'Caveat, cursive' }}>
                {title.split(' ').map((word, index) => (
                  <span key={index}>
                    {index === 0 ? (
                      <>
                        <span style={{ fontSize: '1.1em' }}>{word.charAt(0)}</span>
                        {word.slice(1)}
                      </>
                    ) : (
                      word
                    )}
                    {index === 0 && <span style={{ marginLeft: '0.3em' }}></span>}
                    {index === 1 && <span style={{ marginLeft: '0.5em' }}></span>}
                    {index > 1 && index < title.split(' ').length - 1 && ' '}
                  </span>
                ))}
              </div>
              {translationName && (
                <div className="mt-0.5 text-[9px] sm:text-[10px] font-medium tracking-wide normal-case text-[#4A9BC7]">
                  {translationName}
                </div>
              )}
            </div>
          </div>
          
          {/* Tape Window - Shows the actual tape and reels */}
          <div className="w-full h-24 sm:h-32 bg-[#ebebeb] relative flex justify-center items-start pt-1 sm:pt-2">
            {/* Rectangular tape window container */}
            <div className="w-[12em] sm:w-[18em] h-[4rem] sm:h-[6rem] bg-gray-800 rounded-[14em] relative flex justify-center items-center z-10">
              {/* Tape Reels with Gear Cogs - Closer together */}
              <div className="w-full h-full flex justify-center items-center space-x-4 sm:space-x-8">
                {/* Left Reel with Gear Cogs */}
                <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-primary-700 border-2 sm:border-4 border-primary-600 rounded-full relative ${
                  isRecording && !isPaused ? 'animate-spin' : ''
                }`} style={{
                  animationDuration: '2s',
                  animationTimingFunction: 'linear'
                }}>
                  {/* Gear Cogs - Multiple spokes for realistic look */}
                  <div className="absolute inset-1 sm:inset-2 bg-primary-600 rounded-full"></div>
                  <div className="absolute inset-2 sm:inset-4 bg-primary-500 rounded-full"></div>
                  <div className="absolute inset-3 sm:inset-6 bg-primary-400 rounded-full"></div>
                  {/* Center hub */}
                  <div className="absolute w-3 h-3 sm:w-5 sm:h-5 bg-primary-300 rounded-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
                  {/* Gear teeth/spokes */}
                  <div className="absolute inset-0.5 sm:inset-1 w-0.5 h-full bg-primary-500 mx-auto"></div>
                  <div className="absolute inset-0.5 sm:inset-1 w-full h-0.5 bg-primary-500 my-auto"></div>
                  <div className="absolute inset-0.5 sm:inset-1 w-0.5 h-full bg-primary-500 mx-auto transform rotate-45"></div>
                  <div className="absolute inset-0.5 sm:inset-1 w-full h-0.5 bg-primary-500 my-auto transform rotate-45"></div>
                </div>
                
                {/* Tape Path */}
                <div className="w-12 h-1.5 sm:w-16 sm:h-2 bg-gray-600 rounded-full"></div>
                
                {/* Right Reel with Gear Cogs */}
                <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-primary-700 border-2 sm:border-4 border-primary-600 rounded-full relative ${
                  isRecording && !isPaused ? 'animate-spin' : ''
                }`} style={{
                  animationDuration: '2s',
                  animationTimingFunction: 'linear'
                }}>
                  {/* Gear Cogs - Multiple spokes for realistic look */}
                  <div className="absolute inset-1 sm:inset-2 bg-primary-600 rounded-full"></div>
                  <div className="absolute inset-2 sm:inset-4 bg-primary-500 rounded-full"></div>
                  <div className="absolute inset-3 sm:inset-6 bg-primary-400 rounded-full"></div>
                  {/* Center hub */}
                  <div className="absolute w-3 h-3 sm:w-5 sm:h-5 bg-primary-300 rounded-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
                  {/* Gear teeth/spokes */}
                  <div className="absolute inset-0.5 sm:inset-1 w-0.5 h-full bg-primary-500 mx-auto"></div>
                  <div className="absolute inset-0.5 sm:inset-1 w-full h-0.5 bg-primary-500 my-auto"></div>
                  <div className="absolute inset-0.5 sm:inset-1 w-0.5 h-full bg-primary-500 mx-auto transform rotate-45"></div>
                  <div className="absolute inset-0.5 sm:inset-1 w-full h-0.5 bg-primary-500 my-auto transform rotate-45"></div>
                </div>
              </div>
            </div>
            
            {/* Color Bars Left - Stretch from edge to pill */}
            <div className="absolute left-0 top-2 flex flex-col gap-0.5 z-0">
              <div className="w-[12rem] h-[0.7rem] bg-cerulean"></div>
              <div className="w-[12rem] h-[0.7rem] bg-coral"></div>
              <div className="w-[12rem] h-[0.7rem] bg-aquamarine"></div>
              <div className="w-[12rem] h-[0.7rem] bg-space-cadet"></div>
            </div>
            
            {/* Color Bars Right - Stretch from edge to pill */}
            <div className="absolute right-0 top-2 flex flex-col gap-0.5 z-0">
              <div className="w-[12rem] h-[0.7rem] bg-cerulean"></div>
              <div className="w-[12rem] h-[0.7rem] bg-coral"></div>
              <div className="w-[12rem] h-[0.7rem] bg-aquamarine"></div>
              <div className="w-[12rem] h-[0.7rem] bg-space-cadet"></div>
            </div>

            {/* Recorded-of-total tally for the current scope (chapter or plan) */}
            {(recordedCount !== null && totalVerses !== null) && (
              <div className="absolute bottom-0.5 sm:bottom-1 left-0 right-0 z-10 flex items-center justify-center gap-2 sm:gap-3 text-[10px] sm:text-xs font-medium">
                <span className="text-cerulean">
                  <span className="font-bold">{recordedCount}</span> of <span className="font-bold">{totalVerses}</span> verses recorded
                </span>
              </div>
            )}
          </div>
          
                                {/* Dark bottom section - touches borders with no padding */}
                      <div className="absolute bottom-0 left-0 right-0 h-16 sm:h-20 bg-[#052a4a] rounded-t-lg">
            {/* Plan Name Display - Only show if planName exists */}
            {planName && (
              <div className="absolute top-2 sm:top-2 w-full text-center">
                <div className="text-xs sm:text-sm text-gray-300 font-medium">
                  {planName}
                </div>
              </div>
            )}
            
            {/* Timer Display with Sound Waves */}
            <div className="absolute bottom-3 sm:bottom-5 w-full flex items-center justify-center text-white">
              {/* Left Sound Wave */}
              <div className="mr-3 sm:mr-4">
                <SoundWave 
                  isActive={isRecording && !isPaused}
                  barCount={8}
                  height="20px"
                  color="#4A90E2"
                  className="opacity-60"
                />
              </div>
              
              {/* Timer Display - Only show when recording or paused */}
              {(isRecording || isPaused) && (
                <div className="text-xl sm:text-2xl font-mono font-bold text-white">
                  {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                </div>
              )}
              
              {/* Right Sound Wave */}
              <div className="ml-3 sm:ml-4">
                <SoundWave 
                  isActive={isRecording && !isPaused}
                  barCount={8}
                  height="20px"
                  color="#4A90E2"
                  className="opacity-60"
                />
              </div>
            </div>
          </div>
          
          {/* Screw Holes in Corners */}
          <div className="absolute top-2 left-2 w-3 h-3 bg-gray-600 rounded-full"></div>
          <div className="absolute top-2 right-2 w-3 h-3 bg-gray-600 rounded-full"></div>
          <div className="absolute bottom-2 left-2 w-3 h-3 bg-gray-600 rounded-full"></div>
          <div className="absolute bottom-2 right-2 w-3 h-3 bg-gray-600 rounded-full"></div>
        </div>
        
        {/* Custom CSS for tape movement animation */}
        <style jsx="true">{`
          @keyframes tape-move {
            0% { background-position: 0 0; }
            100% { background-position: -100px 0; }
          }
          
          .animate-tape-move {
            animation: tape-move 2s linear infinite;
          }
        `}</style>
      </div>
    </div>
  );
};

export default RetroCassette;
